<template>
  <QPage>
    <SignupForm :prefilled-email="prefilledEmail" @on-signup="onSignup" @go-to-login="goToLogin" />
    <IconTextConfirmModal
      v-model="emailExistModalOpened"
      :confirm="$t('ETAB.PAGES.SIGNUP.MODAL_EMAIL_EXIST.@CONNECT')"
      :refuse="$t('ETAB.PAGES.SIGNUP.MODAL_EMAIL_EXIST.@FORGET_PASSWORD')"
      :text="$t('ETAB.PAGES.SIGNUP.MODAL_EMAIL_EXIST.@DESCRIPTION', { email })"
      :on-confirm-modal="() => goToLogin({ email })"
      :on-close-modal="() => forgetPassword({ email })"
      :backdrop-dismiss="true"
    />
  </QPage>
</template>

<script>
  import { IconTextConfirmModal } from "@core/components/modals";
  import { EtabSignupService } from "@core/services";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { AuthenticationManager } from "@core/managers/authentication.manager";
  import { PosthogManager } from "@etab/managers/posthog.manager";
  import { TrackingManager } from "@core/managers/tracking.manager";
  import { DataLayerManager } from "@core/managers/datalayer-manager";
  import { ACTION_SIGNUP_CREATE } from "@etab/stores/types";
  import { useSignupStore } from "@etab/stores/signup";
  import { ETAB_ROUTES } from "@etab/config";
  import { CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE } from "@core/store/types";
  import { useFeatureTogglesStore } from "@core/store/modules/featureToggles";
  import { FEATURE_TOGGLES, FIREBASE_EVENTS } from "@core/constants";
  import { ISignup, SignupModel } from "@core/models";
  import { MetaHelpers } from "@core/helpers";
  import { UserStorageManager } from "@core/managers/user-storage.manager";
  import { CookieConsentManager } from "@core/managers/cookie-consent.manager";

  import SignupForm from "@etab/components/auth/SignupForm.component.vue";

  export default {
    components: {
      SignupForm,
      IconTextConfirmModal,
    },
    meta() {
      return MetaHelpers.setAutoMeta({
        description: this.$t("ETAB.PAGES.SIGNUP.META.@DESCRIPTION"),
        canonical: `${window.location.origin}${window.location.pathname}${window.location.hash.split("?")[0]}`,
      });
    },
    data() {
      return {
        emailExistModalOpened: false,
        email: undefined,
      };
    },
    computed: {
      prefilledEmail() {
        return this.$route.query.prefilledEmail;
      },
      posthogOnboardingEventsEnabled() {
        const featureTogglesStore = useFeatureTogglesStore();
        return featureTogglesStore[CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE](FEATURE_TOGGLES.POSTHOG_ETAB_ONBOARDING);
      },
    },
    mounted() {
      // safe guard in case of coming from another call or problem during startup
      this.$_stopGlobalLoading();
    },
    methods: {
      openEmailExistModal() {
        this.emailExistModalOpened = true;
      },
      closeEmailExistModal() {
        this.emailExistModalOpened = false;
      },
      async onSignup(email, optin) {
        const signupStore = useSignupStore();
        try {
          this.$_startGlobalLoading();
          if (email) {
            // publish the email to the template
            this.email = email;
            const doesEmailExist = await EtabSignupService.doesEmailExist({ email: this.email });
            if (doesEmailExist) {
              this.openEmailExistModal();
            } else {
              const originTracker = DataLayerManager.getUrlParamCampaign();
              const utmTrackerFromLocalStorage = await UserStorageManager.getUtmTracker();
              const modelData = { [ISignup.email]: email, [ISignup.optIn]: optin };
              if (originTracker !== undefined && !utmTrackerFromLocalStorage) {
                UserStorageManager.setUtmTracker(originTracker);
              }
              const model = new SignupModel(modelData);
              await signupStore[ACTION_SIGNUP_CREATE](model);
              this.$router.replace({ name: ETAB_ROUTES.signup.steps.main.name });
              TrackingManager.addEvent({
                event: "funnel_start",
              });
              TrackingManager.addEvent({
                event: FIREBASE_EVENTS.FUNNEL,
                step: "step1_email",
              });
              await CookieConsentManager.checkAndShowBanner();
              PosthogManager.captureEvent("sign_up", { $email: email, $is_optout: !optin, $step: ETAB_ROUTES.signup.steps.main.name });
            }
          }
        } catch (error) {
          NotificationsManager.contactEtabSupport();
          throw error;
        } finally {
          this.$_stopGlobalLoading();
        }
      },
      goToLogin({ email } = {}) {
        const prefilledEmail = email;
        this.$router.push({ name: ETAB_ROUTES.auth.login.name, query: { prefilledEmail } }).catch((suppressedError) => {});
      },
      async forgetPassword({ email }) {
        try {
          this.$_startGlobalLoading();
          await AuthenticationManager.generateMagicLink({ email });
          NotificationsManager.success({ message: "SUCCESS.@FORGOT_PASSWORD_EMAIL_SENT" });
          this.goToLogin({ email });
        } catch (error) {
          const status = error && error.request && error.request.status;
          switch (status) {
            case 403:
              // email does not exist
              NotificationsManager.error({ message: "ERRORS.@FORGOT_PASSWORD_EMAIL_NOT_EXIST" });
              break;
            default:
              NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
              break;
          }
          throw error;
        } finally {
          this.closeEmailExistModal();
          this.$_stopGlobalLoading();
        }
      },
    },
  };
</script>
